import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Clock, Facebook, Instagram, Linkedin } from "lucide-react";
import logo from "../../assets/image/logo1.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check if screen is mobile on load and when resized
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "SERVICES", path: "/servicepage" },
    { name: "ABOUT", path: "/about" },
    // { name: "GALLERY", path: "/gallery" },
    { name: "BLOG", path: "/blog/:id" },
    { name: "CONTACT", path: "/contact" },
  ];

  return (
    <nav className="bg-white shadow-lg relative w-full">
      {/* Top Header Line with enhanced gradient */}
      <div className="bg-gradient-to-r from-primary via-secondary to-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center py-2 sm:h-12">
            {/* Contact info - stacked on mobile */}
            <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-2 sm:space-y-0 w-full sm:w-auto items-center sm:items-start mb-2 sm:mb-0">
              <div className="flex items-center text-white hover:text-gray-100 transition-colors duration-300 group">
                <Phone className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-sm text-center sm:text-left">
                  Call: Mob : 0452298814 Tel : 0882708801
                </span>
              </div>
              <div className="flex items-center text-white hover:text-gray-100 transition-colors duration-300 group">
                <Clock className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-sm">24/7 Service Available</span>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center space-x-4 sm:space-x-6">
              <a
                href="https://www.facebook.com/share/1DY3FKLavf/?mibextid=wwXIfr"
                className="text-white hover:text-gray-100 transition-transform duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 sm:h-6 sm:w-6" />
              </a>
              <a
                href="https://www.instagram.com/invites/contact/?i=1q2iwwe4b29x8&utm_content=uilkl3c"
                className="text-white hover:text-gray-100 transition-transform duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
              </a>
              <a
                href="#"
                className="text-white hover:text-gray-100 transition-transform duration-300 hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 sm:h-6 sm:w-6" />
              </a>
              <a
                href="https://www.tiktok.com/@envipro.solutions?_t=ZS-8tHdbJFDRa6&_r=1"
                className="text-white hover:text-gray-100 transition-transform duration-300 hover:scale-110"
                aria-label="TikTok"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05 6.33 6.33 0 0 0-4.27 1.89 6.21 6.21 0 0 0-1.55 4.46c.07 2.12.74 3.78 2.2 4.78a6.3 6.3 0 0 0 3.55 1.13c.66 0 1.32-.1 1.98-.3a6.31 6.31 0 0 0 3.26-2.23 6.18 6.18 0 0 0 1.21-3.74v-6.7a8.09 8.09 0 0 0 4.97 1.89V7.5a4.28 4.28 0 0 1-2.5-1.26z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20 md:h-24">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0">
                <img
                  className="h-16 w-auto sm:h-20 md:h-28 py-2 transition-transform duration-300 hover:scale-105"
                  src={logo}
                  alt="Envipro Solutions"
                />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center flex-wrap justify-end">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`ml-4 lg:ml-8 text-base lg:text-lg font-medium transition-all duration-300 hover:text-primary relative group
                    ${
                      location.pathname === link.path
                        ? "text-primary"
                        : "text-gray-700"
                    }`}
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></span>
                </Link>
              ))}
              <Link
                to="/contact"
                className="ml-4 lg:ml-8 text-base lg:text-lg font-medium px-4 sm:px-6 lg:px-8 py-2 lg:py-3 rounded-md text-white bg-gradient-to-r from-primary to-secondary hover:from-primary-dark hover:to-secondary-dark transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                Get Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-700 hover:text-primary focus:outline-none transition-colors duration-300"
                aria-label={isOpen ? "Close menu" : "Open menu"}
              >
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu with animated transition */}
      <div 
        className={`md:hidden bg-gradient-to-b from-white to-gray-50 absolute w-full z-50 shadow-lg transform transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10 pointer-events-none"
        }`}
        style={{ maxHeight: isOpen ? "100vh" : "0", overflow: "hidden" }}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`block px-3 py-2 text-base font-medium rounded-md transition-all duration-300 hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10
                ${
                  location.pathname === link.path
                    ? "text-primary bg-gradient-to-r from-primary/5 to-secondary/5"
                    : "text-gray-700"
                }`}
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/contact"
            className="block w-full text-center px-3 py-3 text-base font-medium text-white bg-gradient-to-r from-primary to-secondary hover:from-primary-dark hover:to-secondary-dark rounded-md transition-all duration-300 mt-4"
            onClick={() => setIsOpen(false)}
          >
            Get Quote
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;